import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { IonIcon } from '@ionic/react';
import { restaurantOutline, closeOutline } from 'ionicons/icons';
import { Avatar } from '../ui/Avatar';
import { UserAttribution, type UserAttributionSummary } from '../ui/UserAttribution';
import { useActivitySheetStore } from '../../store/useActivitySheetStore';
import { isOrderVisible, scrollToOrderCard } from './orderVisibility';

const DEFAULT_DURATION_MS = 5000;
// Upward travel (px) past which a touch drag dismisses the toast.
const SWIPE_DISMISS_THRESHOLD = 40;

// Placeholder embedded in a subtitle string (right where the name starts) that
// marks where the avatar should render inline, just before the name.
// Callers inject it via the i18n `name` param; the toast splits on it. A
// private-use codepoint so it never collides with real copy.
export const AVATAR_MARKER = '\uE000';

/**
 * Render a subtitle, inserting the person attribution (pfp → name → connection
 * badge) inline at the AVATAR_MARKER so it reads "… from [pfp] Jane 🏠". The
 * marker stands in for the whole person — the i18n template passes the marker as
 * its `{{name}}` and the name itself comes from the summary, so the same
 * UserAttribution component used on cards renders here. No marker (or no
 * attribution) → plain text (e.g. "Order cancelled"). White pill on the badge so
 * the ordinal stays legible on the brand-colored toast.
 */
function renderSubtitle(
  subtitle: string,
  attribution?: UserAttributionSummary,
): React.ReactNode {
  const idx = subtitle.indexOf(AVATAR_MARKER);
  if (idx === -1 || !attribution) return subtitle;
  return (
    <>
      {subtitle.slice(0, idx)}
      <UserAttribution
        summary={attribution}
        inline
        size="xs"
        nameClassName="font-medium text-white"
      />
      {subtitle.slice(idx + AVATAR_MARKER.length)}
    </>
  );
}

// Maps an order to its currently-shown toast id so a surface that renders that
// order (its card, or the order-in-progress page) can dismiss the toast when the
// user is clearly already looking at the order. Stale entries are harmless —
// dismissing an already-gone toast id is a no-op.
const orderToastIds = new Map<string, string>();

/** Dismiss the toast (if any) currently shown for this order. */
export function dismissOrderToast(orderId: string): void {
  const id = orderToastIds.get(orderId);
  if (!id) return;
  toast.dismiss(id);
  orderToastIds.delete(orderId);
}

interface OrderActivityToastArgs {
  /** Order whose card the sheet should scroll to when tapped. */
  orderId: string;
  /** Listing title shown on the right of the banner. */
  title: string;
  /** Listing thumbnail (falls back to the full image, then a placeholder). */
  thumbnailUrl?: string;
  /** Brief line under the title, e.g. "New order from Jane D." The
   * AVATAR_MARKER in it is replaced by the `attribution` person unit. */
  subtitle?: string;
  /** The person referenced in the subtitle (orderer/chef). Rendered inline at
   * the AVATAR_MARKER as pfp → name → connection badge. */
  attribution?: UserAttributionSummary;
  /** react-hot-toast id — share `ws:activity-count` so it replaces the
   * plain count toast and is cleared by the same dismiss call. */
  id: string;
  duration?: number;
  /** Persist until the user taps/swipes/X's it — no auto-dismiss, no timer bar
   * (forces duration to Infinity). Softened to non-sticky when the order's card
   * is already visible (see showOrderActivityToast). */
  sticky?: boolean;
  /** Like `sticky` but unconditional — stays even when the order card is already
   * visible. Use for prods the user must acknowledge (e.g. the "mark it ready"
   * reminder), not passive status toasts. */
  forceSticky?: boolean;
}

interface ActivityToastBannerProps {
  /** Headline shown on the right of the banner. */
  title: string;
  /** Listing thumbnail (4:3) shown on the left. Order toasts use this. */
  thumbnailUrl?: string;
  /** Person avatar (circle) shown on the left when there's no thumbnail —
   * used by person-centric toasts like connections. */
  leadingAvatarUrl?: string;
  /** Brief line under the title. */
  subtitle?: string;
  /** Person rendered inline within the subtitle at AVATAR_MARKER. */
  attribution?: UserAttributionSummary;
  id: string;
  duration?: number;
  visible: boolean;
  onTap: () => void;
  /** react-hot-toast's `createdAt`, which bumps on every in-place update. Drives
   * both the countdown bar's key and the auto-dismiss timer's deps so a toast
   * replacing an existing one (same id) restarts the bar AND the timer. */
  timerKey?: number;
}

/**
 * The rich activity banner: a thumbnail (orders) or person avatar
 * (connections) on the left, title on the right. The tap behavior is
 * supplied by the caller via `onTap`. Theme-aware (semantic color classes)
 * so it reads correctly in light/dark.
 */
const ActivityToastBanner: React.FC<ActivityToastBannerProps> = ({
  title,
  thumbnailUrl,
  leadingAvatarUrl,
  subtitle,
  attribution,
  duration,
  timerKey,
  id,
  visible,
  onTap,
}) => {
  // Swipe-up to dismiss (mobile). Threshold-based — once the touch travels far
  // enough up, dismiss; the exit animation handles the visual fly-away.
  const touchStartY = useRef<number | null>(null);
  const swiped = useRef(false);

  const dismiss = () => toast.dismiss(id);

  // Self-manage auto-dismiss (react-hot-toast's own duration is set to Infinity
  // for these toasts). Its pause-on-hover can get stuck on touch — a stray
  // mouseenter over the toaster with no mouseleave pauses every timer, so the
  // CSS bar finishes but the toast never leaves. A plain timer can't get stuck.
  // Resets with `timerKey` so an in-place replace restarts it alongside the bar.
  useEffect(() => {
    if (duration === undefined || !Number.isFinite(duration)) return;
    const t = setTimeout(() => toast.dismiss(id), duration);
    return () => clearTimeout(t);
  }, [id, duration, timerKey]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    swiped.current = false;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current === null || swiped.current) return;
    if (touchStartY.current - e.touches[0].clientY > SWIPE_DISMISS_THRESHOLD) {
      swiped.current = true;
      dismiss();
    }
  };
  // A swipe ends in a synthetic click — guard so it doesn't also open the sheet.
  const handleActivate = () => {
    if (swiped.current) return;
    onTap();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onTap();
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      className={[
        // Brand-colored in both themes so the toast never blends into the app
        // background. brand-primary is the same in light/dark and its contrast is
        // white, so white text/tints read correctly in both.
        // Prominent downward drop shadow to lift the toast off the app.
        // `relative overflow-hidden` so the countdown bar pins to the bottom edge
        // and is clipped to the rounded corners.
        'relative cursor-pointer select-none overflow-hidden flex w-full max-w-sm items-center gap-3 rounded-xl border border-white/20 bg-brand-primary p-3 text-left text-white shadow-[0_18px_40px_-8px_rgba(0,0,0,0.55)]',
        // Slide down from the top on enter, slide back up on leave. Keyframe-based
        // (tailwindcss-animate) rather than a `visible`-toggled transition so the
        // ENTER animation actually plays on mount, not just the exit.
        // `fill-mode-forwards` on the exit keeps the toast in its slid-out state
        // until react-hot-toast unmounts it (~1s after `visible` flips). Without
        // it, the 200ms exit finishes and the element snaps back to its resting
        // (visible) state for the remaining delay — reading as a second "enter".
        visible
          ? 'animate-in fade-in slide-in-from-top-16 duration-300 ease-out'
          : 'animate-out fade-out slide-out-to-top-16 fill-mode-forwards duration-200 ease-in',
      ].join(' ')}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt=""
          // Listing images are ~4:3 — render at that ratio so they aren't
          // cropped into a square. w-20 + aspect-[4/3] => 80x60px.
          className="aspect-[4/3] w-20 flex-shrink-0 rounded-lg object-cover"
        />
      ) : leadingAvatarUrl !== undefined ? (
        // Person-centric (connections): a round avatar reads as "someone",
        // not "a dish". Renders the initials fallback when the url is empty.
        <Avatar src={leadingAvatarUrl} size="lg" className="!h-14 !w-14 flex-shrink-0" />
      ) : (
        <div className="flex aspect-[4/3] w-20 flex-shrink-0 items-center justify-center rounded-lg bg-white/10 text-white/70">
          <IonIcon icon={restaurantOutline} className="text-2xl" />
        </div>
      )}
      {/* pr-5 leaves room so the title never runs under the dismiss X. Text
          wraps to as many lines as needed; the toast grows to fit. */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pr-5">
        <span className="font-heading text-sm font-semibold text-white break-words">
          {title}
        </span>
        {subtitle && (
          <div className="text-xs text-white/80 break-words">
            {renderSubtitle(subtitle, attribution)}
          </div>
        )}
      </div>

      {/* Dismiss — stopPropagation so it doesn't also trigger the tap-to-open. */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={(e) => {
          e.stopPropagation();
          dismiss();
        }}
        className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/15 hover:text-white"
      >
        <IonIcon icon={closeOutline} className="text-base" />
      </button>

      {/* Countdown bar — only for auto-dismissing toasts (finite duration). A
          sticky toast (Infinity) shows none. Animation length matches `duration`
          so it empties just as the toast starts to dismiss. */}
      {duration !== undefined && Number.isFinite(duration) && (
        <span
          key={timerKey}
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-1 origin-left bg-white/40"
          style={{ animation: `hp-toast-timer ${duration}ms linear forwards` }}
        />
      )}
    </div>
  );
};

/**
 * Show the rich order-activity toast. Reuses the count toast's id so it
 * collapses with / replaces the plain text toast and is cleared when the
 * unread count drops to zero (`notify.dismiss('ws:activity-count')`).
 */
export function showOrderActivityToast({
  orderId,
  title,
  thumbnailUrl,
  subtitle,
  attribution,
  id,
  duration = DEFAULT_DURATION_MS,
  sticky,
  forceSticky,
}: OrderActivityToastArgs): void {
  // A toast for an order the user is already looking at shouldn't be sticky —
  // let it auto-dismiss instead of persisting over the visible card. `forceSticky`
  // opts out of that softening for prods that must be acknowledged.
  const effectiveSticky = forceSticky || (sticky && !isOrderVisible(orderId));
  // Sticky toasts never auto-dismiss — Infinity disables react-hot-toast's
  // timer and (being non-finite) suppresses the countdown bar.
  const effectiveDuration = effectiveSticky ? Infinity : duration;

  // Register so a surface showing this order can auto-dismiss the toast.
  orderToastIds.set(orderId, id);

  // If the sheet is already open, pull focus to this order now — same outcome
  // as tapping the toast. (When closed, tapping handles it via openToOrder.)
  const sheet = useActivitySheetStore.getState();
  if (sheet.isOpen) {
    sheet.setScrollTarget(orderId);
  }

  toast.custom(
    (tst) => (
      <ActivityToastBanner
        title={title}
        thumbnailUrl={thumbnailUrl}
        subtitle={subtitle}
        attribution={attribution}
        duration={effectiveDuration}
        timerKey={tst.createdAt}
        id={id}
        visible={tst.visible}
        onTap={() => {
          toast.dismiss(tst.id);
          // If the order's card is already on the current page (e.g. the lister
          // on listing details), scroll to it in place; otherwise open the
          // activity sheet and scroll there.
          if (!scrollToOrderCard(orderId)) {
            useActivitySheetStore.getState().openToOrder(orderId);
          }
        }}
      />
    ),
    // Infinity: react-hot-toast never auto-dismisses these — the content's own
    // timer (synced to the bar) owns dismissal, immune to stuck pause-on-hover.
    { id, duration: Infinity },
  );
}

interface ListingStatusToastArgs {
  /** Listing title shown on the right of the banner. */
  title: string;
  /** Listing thumbnail (falls back to the full image, then a placeholder). */
  thumbnailUrl?: string;
  /** Status line under the title, e.g. "Your pickup window is now open". */
  subtitle: string;
  /** Shared toast id (`ws:activity-count`) so it replaces the plain count
   * toast and is cleared by the same dismiss call. */
  id: string;
  duration?: number;
}

/**
 * Listing-status toast (went live / ended). Listing-centric: a 4:3 thumbnail on
 * the left (same treatment as order toasts) and a status subtitle — no person,
 * since these are system transitions with no actor. Tapping opens the activity
 * sheet, where the matching bell entry (a generic notification card) lives and
 * routes on to the listing. Only the lister receives these events, so there's
 * no self-toast to suppress.
 */
export function showListingStatusToast({
  title,
  thumbnailUrl,
  subtitle,
  id,
  duration = DEFAULT_DURATION_MS,
}: ListingStatusToastArgs): void {
  toast.custom(
    (tst) => (
      <ActivityToastBanner
        title={title}
        thumbnailUrl={thumbnailUrl}
        subtitle={subtitle}
        duration={duration}
        timerKey={tst.createdAt}
        id={id}
        visible={tst.visible}
        onTap={() => {
          toast.dismiss(tst.id);
          useActivitySheetStore.getState().open();
        }}
      />
    ),
    { id, duration: Infinity },
  );
}

interface ConnectionActivityToastArgs {
  /** Headline, e.g. "Jane wants to connect" (already localized by the BE). */
  title: string;
  /** The other person's avatar — shown as the leading circle. */
  avatarUrl?: string;
  /** Shared toast id (`ws:activity-count`) so it replaces the plain count
   * toast and is cleared by the same dismiss call. */
  id: string;
  duration?: number;
}

/**
 * Connection-activity toast (request / accepted). Person-centric: a round
 * avatar on the left and the BE-localized headline. Tapping opens the
 * activity sheet — the connection card there routes on to `/c={id}`.
 */
export function showConnectionActivityToast({
  title,
  avatarUrl,
  id,
  duration = DEFAULT_DURATION_MS,
}: ConnectionActivityToastArgs): void {
  toast.custom(
    (tst) => (
      <ActivityToastBanner
        title={title}
        leadingAvatarUrl={avatarUrl ?? ''}
        duration={duration}
        timerKey={tst.createdAt}
        id={id}
        visible={tst.visible}
        onTap={() => {
          toast.dismiss(tst.id);
          useActivitySheetStore.getState().open();
        }}
      />
    ),
    { id, duration: Infinity },
  );
}
